import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DateTime } from 'luxon';
import * as moment from 'moment';
import { ProjectService } from './project.service';

/** Naive SQL-style datetime: no timezone in string */
const NAIVE_FMT = 'yyyy-MM-dd HH:mm:ss';
const NAIVE_REGEX = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

/** Locale- and timezone-aware labels for screenshot cards (SaaS-style). */
export interface ScreenshotTimeView {
  /** Primary line: this instant in the viewer’s local timezone, locale-formatted */
  primaryAbsolute: string;
  /** Second line when team (shift) zone differs from viewer’s zone */
  secondaryLine?: string;
  /** Multi-line hover: timezone context + raw DB string (no duplicate of the card line) */
  tooltip: string;
}

/** Card + lightbox footer: primary line, card tooltip, relative-time inputs */
export interface ScreenshotTimeEnrichment {
  timeDisplay: ScreenshotTimeView;
  timeagoInput: Date | null;
  showAbsoluteTime: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ScreenshotTimeService {
  constructor(private projectService: ProjectService) {}

  /** First shift's IANA timezone for the company, or null */
  getShiftTimezoneForCompany(companyId: number | undefined | null): Observable<string | null> {
    if (companyId == null || companyId === undefined) {
      return of(null);
    }
    return this.projectService.getShifts(companyId).pipe(
      map((res: any) => {
        const shifts = res?.shifts ?? res?.data?.shifts ?? [];
        const first = Array.isArray(shifts) ? shifts[0] : null;
        const tz = first?.timezone;
        return tz && this.isValidIanaZone(tz) ? tz : null;
      }),
      catchError(() => of(null))
    );
  }

  isValidIanaZone(zone: string): boolean {
    if (!zone || typeof zone !== 'string') {
      return false;
    }
    try {
      const z = DateTime.now().setZone(zone);
      return z.isValid;
    } catch {
      return false;
    }
  }

  /**
   * Parse capture time to UTC DateTime.
   * Priority: explicit ISO/offset → naive + shift zone → legacy browser-local naive.
   */
  parseCaptureToUtc(
    raw: string | number | Date | null | undefined,
    shiftTimezone?: string | null,
    record?: { timezone?: string; tz?: string }
  ): DateTime | null {
    if (raw == null || raw === '') {
      return null;
    }
    if (typeof raw === 'number') {
      const dt = DateTime.fromMillis(raw, { zone: 'utc' });
      return dt.isValid ? dt : null;
    }
    if (raw instanceof Date) {
      const dt = DateTime.fromJSDate(raw);
      return dt.isValid ? dt.toUTC() : null;
    }

    const s = String(raw).trim();
    if (!s) {
      return null;
    }

    const explicitZone = record?.timezone || record?.tz;
    if (explicitZone && this.isValidIanaZone(explicitZone) && NAIVE_REGEX.test(s)) {
      const dt = DateTime.fromFormat(s, NAIVE_FMT, { zone: explicitZone });
      return dt.isValid ? dt.toUTC() : null;
    }

    let dt = DateTime.fromISO(s, { setZone: true });
    if (dt.isValid) {
      return dt.toUTC();
    }

    if (NAIVE_REGEX.test(s)) {
      if (shiftTimezone && this.isValidIanaZone(shiftTimezone)) {
        const zoned = DateTime.fromFormat(s, NAIVE_FMT, { zone: shiftTimezone });
        if (zoned.isValid) {
          return zoned.toUTC();
        }
      }
      const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const legacy = DateTime.fromFormat(s, NAIVE_FMT, { zone: localZone });
      return legacy.isValid ? legacy.toUTC() : null;
    }

    const fallback = DateTime.fromJSDate(new Date(s));
    return fallback.isValid ? fallback.toUTC() : null;
  }

  /**
   * Rich display for cards: Intl date/time in user locale, optional team line, detailed tooltip.
   */
  getScreenshotTimeView(raw: string | number | Date | null | undefined, shiftTimezone?: string | null): ScreenshotTimeView | null {
    if (raw == null || raw === '') {
      return null;
    }
    const utc = this.parseCaptureToUtc(raw, shiftTimezone);
    const rawStr = String(raw).trim();
    if (!utc || !utc.isValid) {
      return {
        primaryAbsolute: rawStr,
        tooltip: rawStr,
      };
    }

    const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const primaryAbsolute = this.formatInstantForViewerLocale(utc);
    let secondaryLine: string | undefined;
    if (shiftTimezone && this.isValidIanaZone(shiftTimezone)) {
      const teamWall = this.formatInstantInTimeZone(utc, shiftTimezone);
      // Only show a second line when the office clock *reads* differently (avoids Asia/Kolkata vs Asia/Calcutta duplicates).
      if (teamWall !== primaryAbsolute) {
        const shortLabel = this.getTimeZoneShortLabel(shiftTimezone);
        secondaryLine = `Office · ${teamWall} · ${shortLabel}`;
      }
    }
    const tooltip = this.buildScreenshotTimeTooltip(utc, rawStr, shiftTimezone, localZone, primaryAbsolute);
    return { primaryAbsolute, secondaryLine, tooltip };
  }

  /** Short tag for UI, e.g. city or IANA last segment */
  private getTimeZoneShortLabel(iana: string): string {
    const parts = iana.split('/');
    return parts.length > 1 ? parts[parts.length - 1].replace(/_/g, ' ') : iana;
  }

  /**
   * Same time fields as Screenshots cards — used by cards, lightbox footer, and Work Replay.
   * Tooltip always uses Your time / Office time / Saved as (card hover format).
   */
  getEnrichedScreenshotTime(
    raw: string | number | Date | null | undefined,
    shiftTimezone?: string | null
  ): ScreenshotTimeEnrichment | null {
    if (raw == null || raw === '') {
      return null;
    }
    const rawStr = String(raw).trim();
    const base = this.getScreenshotTimeView(raw, shiftTimezone);
    const cardTooltip = this.buildCardHoverTooltip(raw, shiftTimezone);
    const timeDisplay: ScreenshotTimeView = base
      ? { ...base, tooltip: cardTooltip }
      : { primaryAbsolute: rawStr, tooltip: cardTooltip };

    return {
      timeDisplay,
      timeagoInput: this.toTimeagoInput(raw, shiftTimezone),
      showAbsoluteTime: this.isCaptureOlderThanMinutes(raw, shiftTimezone),
    };
  }

  /** Card / lightbox tooltip: Your time, Office time (when configured), Saved as */
  buildCardHoverTooltip(
    raw: string | number | Date | null | undefined,
    shiftTimezone?: string | null
  ): string {
    const rawStr = String(raw).trim();
    const utc = this.parseCaptureToUtc(raw, shiftTimezone);
    if (!utc?.isValid) {
      return `Saved as: ${rawStr}`;
    }

    const captureDate = utc.toJSDate();
    const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const viewerTime = this.formatTooltipDateTimeDetailed(captureDate, viewerZone);
    const lines: string[] = [];

    if (shiftTimezone && this.isValidIanaZone(shiftTimezone)) {
      const officeTime = this.formatTooltipDateTimeDetailed(captureDate, shiftTimezone);
      lines.push(`Your time: ${viewerTime}`);
      lines.push(`Office time: ${officeTime}`);
    } else {
      lines.push(`Your time: ${viewerTime}`);
    }

    lines.push(`Saved as: ${rawStr}`);
    return lines.join('\n');
  }

  private formatTooltipDateTimeDetailed(date: Date, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone,
    }).format(date);
  }

  /** Single line for compact lists (employees table, etc.): same primary as cards */
  formatDisplayLocal(raw: string | null | undefined, shiftTimezone?: string | null): string {
    const v = this.getScreenshotTimeView(raw, shiftTimezone);
    return v?.primaryAbsolute ?? (raw == null || raw === '' ? '' : String(raw));
  }

  private formatInstantForViewerLocale(utc: DateTime): string {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(utc.toJSDate());
  }

  private formatInstantInTimeZone(utc: DateTime, ianaZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: ianaZone,
    }).format(utc.toJSDate());
  }

  private getTimeZoneLabel(iana: string): string {
    try {
      // Intl timeZone DisplayNames is supported in modern browsers; TS lib may lag the option type.
      const dn = new Intl.DisplayNames(undefined, { type: 'timeZone' } as any);
      return dn.of(iana) || iana;
    } catch {
      return iana;
    }
  }

  private buildScreenshotTimeTooltip(
    utc: DateTime,
    rawStored: string,
    shiftTimezone: string | null | undefined,
    viewerZone: string,
    primaryLabel: string
  ): string {
    const lines: string[] = [];
    if (shiftTimezone && this.isValidIanaZone(shiftTimezone)) {
      const officeStr = this.formatInstantInTimeZone(utc, shiftTimezone);
      const regionName = this.getTimeZoneLabel(shiftTimezone);
      if (officeStr !== primaryLabel) {
        lines.push(`Your time: ${primaryLabel}`);
        lines.push(`Office (${regionName}): ${officeStr}`);
      } else {
        // Same formatted clock as the card — don’t explain “conversion”; just anchor team context + raw row.
        lines.push(`Company work region: ${regionName}`);
      }
    } else {
      lines.push(`Shown in your device language and time zone.`);
    }
    lines.push(`Saved as: ${rawStored}`);
    return lines.join('\n');
  }

  /**
   * Optional one-line hint for list headers.
   * Shown only when the viewer’s timezone differs from the company work region — otherwise null (avoid banner noise).
   */
  getScreenshotsRegionHint(shiftTimezone: string | null | undefined): string | null {
    if (!shiftTimezone || !this.isValidIanaZone(shiftTimezone)) {
      return null;
    }
    const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (this.zonesAreSameForHint(viewerZone, shiftTimezone)) {
      return null;
    }
    const name = this.getTimeZoneLabel(shiftTimezone);
    return `Captures use ${name}. Times on screen follow your device — both describe the same moment.`;
  }

  /** Same instant/region for hint purposes (e.g. Asia/Calcutta vs Asia/Kolkata). */
  private zonesAreSameForHint(a: string, b: string): boolean {
    if (a === b) {
      return true;
    }
    const aliases: Record<string, string> = {
      'Asia/Calcutta': 'Asia/Kolkata',
    };
    const na = aliases[a] ?? a;
    const nb = aliases[b] ?? b;
    if (na === nb) {
      return true;
    }
    try {
      const now = DateTime.now();
      const za = now.setZone(a);
      const zb = now.setZone(b);
      return za.isValid && zb.isValid && za.offset === zb.offset && za.toFormat('yyyy-MM-dd HH:mm') === zb.toFormat('yyyy-MM-dd HH:mm');
    } catch {
      return false;
    }
  }

  /** Value suitable for ngx-timeago / Date (same instant as capture) */
  toTimeagoInput(
    raw: string | number | Date | null | undefined,
    shiftTimezone?: string | null
  ): Date | null {
    const utc = this.parseCaptureToUtc(raw, shiftTimezone);
    if (!utc || !utc.isValid) {
      return null;
    }
    return utc.toJSDate();
  }

  /**
   * Same semantics as legacy checkTimeForImage: returns true when capture is older than threshold
   * (show raw string), false when recent (show relative).
   */
  isCaptureOlderThanMinutes(
    raw: string | number | Date | null | undefined,
    shiftTimezone: string | null | undefined,
    thresholdMinutes = 10
  ): boolean {
    const utc = this.parseCaptureToUtc(raw, shiftTimezone);
    if (!utc || !utc.isValid) {
      return true;
    }
    const diffMin = DateTime.utc().diff(utc, 'minutes').minutes;
    if (diffMin < -2) {
      return true;
    }
    return diffMin > thresholdMinutes;
  }

  isWithinLast24Hours(raw: string | null | undefined, shiftTimezone: string | null | undefined): boolean {
    const utc = this.parseCaptureToUtc(raw, shiftTimezone);
    if (!utc || !utc.isValid) {
      return false;
    }
    const seconds = DateTime.utc().toSeconds() - utc.toSeconds();
    return seconds >= 0 && seconds < 86400;
  }

  /**
   * Map admin's selected local date range to API start/end strings aligned with stored naive shift-local times.
   * When no shift: preserve previous behavior (moment format in local).
   */
  filterBoundsForApi(
    startMoment: moment.Moment,
    endMoment: moment.Moment,
    shiftTimezone: string | null | undefined
  ): { start: string; end: string } {
    if (!shiftTimezone || !this.isValidIanaZone(shiftTimezone)) {
      return {
        start: startMoment.format('YYYY-MM-DD HH:mm:ss'),
        end: endMoment.format('YYYY-MM-DD HH:mm:ss'),
      };
    }

    // Use the same calendar dates the admin selected, but expressed as shift-local wall time.
    // This avoids cross-timezone day drift (e.g. selected "Apr 6" becoming API "Apr 7").
    const startLocal = DateTime.fromObject(
      {
        year: startMoment.year(),
        month: startMoment.month() + 1,
        day: startMoment.date(),
        hour: 0,
        minute: 0,
        second: 0,
      },
      { zone: shiftTimezone }
    );
    const endLocal = DateTime.fromObject(
      {
        year: endMoment.year(),
        month: endMoment.month() + 1,
        day: endMoment.date(),
        hour: 23,
        minute: 59,
        second: 59,
      },
      { zone: shiftTimezone }
    );
    return {
      start: startLocal.toFormat(NAIVE_FMT),
      end: endLocal.toFormat(NAIVE_FMT),
    };
  }
}
